/**
 * 大厅游戏列表的纯函数：显示名、搜索、副标题。**不碰 DOM，不碰 i18n 实例** ——
 * 为的是能在 node 里直接测。
 */
import type { GameCard, PpLiveFeed } from '../auth/api.ts';

export const PREVIEW_LIMIT = 10;
// Provider keys are uppercase; this internal filter cannot collide with them.
export const HOT_FILTER = '@hot';

/** Stable ties preserve the operator's sort/code order, including new games. */
export function popularGames(games: readonly GameCard[]): GameCard[] {
  return [...games].sort((a, b) => (b.playerCount ?? 0) - (a.playerCount ?? 0));
}

export function hotGames(games: readonly GameCard[]): GameCard[] {
  return popularGames(games).slice(0, PREVIEW_LIMIT);
}

export type LobbyEntry = { game: GameCard; table?: PpLiveFeed['tables'][number] };

/** Limit mounted cards (including live tables), so hidden artwork never loads.
 * Search matches the whole provider catalog before the preview is sliced.
 * Hot always searches the global top ten, not the top ten of search matches. */
export function lobbySections(
  games: readonly GameCard[], query: string, selected: string | null,
  expanded: ReadonlySet<string> = new Set(), tables: PpLiveFeed['tables'] = [],
  live: Readonly<Record<string, { name: string }>> = {},
) {
  const hot = filterGames(hotGames(games), query);
  const sections = selected === null || selected === HOT_FILTER ? [{
    provider: HOT_FILTER, count: hot.length, totalCards: hot.length,
    entries: hot.map((game): LobbyEntry => ({ game })),
  }].filter((section) => section.count > 0) : [];
  if (selected === HOT_FILTER) return sections;
  const byCode = new Map(games.map((g) => [g.code, g]));
  const order = new Map(games.map((g, index) => [g.code, index]));
  return [...sections, ...lobbyGroups(games, query, selected, tables, live).map((group) => {
    const entries: LobbyEntry[] = [
      ...group.games.map((game) => ({ game })),
      ...group.tables.map((table) => ({ game: byCode.get(table.gameCode)!, table })),
    ];
    // Original catalog index also resolves ties between live and ordinary cards.
    entries.sort((a, b) => (b.game.playerCount ?? 0) - (a.game.playerCount ?? 0) ||
      order.get(a.game.code)! - order.get(b.game.code)!);
    return { provider: group.provider, count: group.count, totalCards: entries.length,
      entries: expanded.has(group.provider) ? entries : entries.slice(0, PREVIEW_LIMIT) };
  })];
}

/** Publication and provider readiness are separate; neither changes catalog counts. */
export function playable(g: Pick<GameCard, 'status' | 'available'>): boolean {
  return g.status === 'online' && g.available !== false;
}

/**
 * 这个语言下显示什么名字。**游戏名我们自己不翻**（商品名），
 * 但厂商给了官方译名就用 —— 中文玩家认的是"麻将胡了"，不是 "Mahjong Ways"。
 */
export function gameName(g: Pick<GameCard, 'name' | 'names'>, locale: string): string {
  return g.names?.[locale]?.trim() || g.name;
}

/** 大小写、重音、全半角都不算区别：搜 "pinata" 要能找到 "Piñata"，搜 "ＰＧ" 能找到 "PG" */
function fold(s: string): string {
  return s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * 搜索。**所有语言的名字都参与匹配**，不只当前语言 ——
 * 界面是葡语，但玩家听朋友说的是英文名，这很常见。
 */
export function filterGames<T extends GameCard>(games: readonly T[], query: string): T[] {
  const q = fold(query);
  if (!q) return [...games];
  return games.filter((g) =>
    [g.name, g.provider ?? '', ...Object.values(g.names ?? {})].some((s) => fold(s).includes(q)));
}

/** null is the All filter; an empty provider key is the separate Other group. */
export function providerKey(provider: string | null): string {
  return (provider ?? '').normalize('NFKC').trim().toUpperCase();
}

/** Keep the server's order within each provider and the first-seen provider order. */
export function groupGames(games: readonly GameCard[]): { provider: string; games: GameCard[] }[] {
  const groups = new Map<string, GameCard[]>();
  for (const game of games) {
    const key = providerKey(game.provider);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(game);
  }
  return [...groups].map(([provider, games]) => ({ provider, games }));
}

/** Live tables stay with their provider and replace their ordinary game card.
 * Counts are unique games, even when several live tables launch the same game. */
export function lobbyGroups(
  games: readonly GameCard[], query: string, selected: string | null,
  tables: PpLiveFeed['tables'] = [],
  live: Readonly<Record<string, { name: string }>> = {},
) {
  const matching = new Set(filterGames(games, query).map((g) => g.code));
  const bound = new Set(tables.map((table) => table.gameCode));
  const q = fold(query);
  return groupGames(games).filter((group) => selected === null || group.provider === selected)
    .map((group) => {
      const codes = new Set(group.games.map((g) => g.code));
      const shownTables = tables.filter((table) => codes.has(table.gameCode) &&
        (matching.has(table.gameCode) || fold(live[table.tableId]?.name ?? '').includes(q)));
      const shownGames = group.games.filter((g) => matching.has(g.code) && !bound.has(g.code));
      return {
        provider: group.provider, games: shownGames, tables: shownTables,
        count: new Set([...shownGames.map((g) => g.code), ...shownTables.map((t) => t.gameCode)]).size,
      };
    }).filter((group) => group.count > 0);
}

/**
 * 卡片副标题用哪一条文案。**缺哪段就少哪段**，别显示 "PG · — · 0 线"：
 *
 *     'game.tag'       厂商 · 盘面 · N 线（自研游戏，引擎给得出盘面）
 *     'game.tagLines'  厂商 · N 线（厂商游戏，清单里只有线数）
 *     'game.tagGrid'   厂商 · 盘面（**按路数派彩的游戏根本没有线**，
 *                      比如 Anubis Wrath 的 1024 ways —— 它的 lines 是 0，
 *                      不给这一条的话卡片上就只剩一个 "PG"）
 *     null             只显示厂商名（或者什么都不显示）
 */
export function tagKey(g: Pick<GameCard, 'grid' | 'lines'>):
  'game.tag' | 'game.tagLines' | 'game.tagGrid' | null {
  if (!g.lines) return g.grid ? 'game.tagGrid' : null;
  return g.grid ? 'game.tag' : 'game.tagLines';
}

/** 游戏多到这个数才出搜索框。三五款的时候一个搜索框只是占地方 */
export const SEARCH_FROM = 9;
